import React, { useState, useEffect } from 'react';
import { Check, Zap, Star, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import '../styles/PlanRunamaticPage.css';

const PlanRunamaticPage = () => {
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Scroll al inicio de la página cuando se monta el componente
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const planDetails = {
    name: 'Plan Full',
    description: 'Incluye asesoría completa de estrategia comercial, mejora de procesos y optimización. Nuestro equipo analiza y configura toda la automatización necesaria para tu empresa.',
    usdPrice: 450,
    pesosPrice: 550000,
    features: [
      'Todo lo incluido en el Plan Pro',
      'Asesoría completa de estrategia comercial',
      'Análisis y mejora de procesos comerciales',
      'Optimización continua de consultas y respuestas',
      'Configuración 100% personalizada por nuestro equipo',
      'Grupo de WhatsApp dedicado con nuestro equipo',
      'Reunión inicial de onboarding y análisis de tu marca',
      'Propuesta de soluciones tecnológicas a medida',
      'Demo antes de conectar a canales reales',
      'Conexión a canales reales (WhatsApp, Instagram, TikTok, Telegram, etc.)',
      'Refinamiento continuo y actualizaciones a pedido',
      'Posibilidad de upgrades e integraciones con sistemas externos'
    ],
    additionalFeatures: [
      'Todas las herramientas de la plataforma con IA incluida',
      'Respuestas de preguntas frecuentes automatizadas',
      'Filtro de tráfico de clientes y derivación hacia canales de ventas',
      'Envío de archivos y catálogos de productos',
      'Inteligencia Artificial con OpenAI integrada',
      'Tipo de respuestas: Texto, Audio e Imágenes',
      'Mensajería masiva multicanal',
      'CRM omnicanal con usuarios y contactos ilimitados'
    ],
    paymentUrls: {
      pesos: 'https://mpago.la/1Naaky3',
      usd: 'https://buy.stripe.com/28E28r9F43r87Qd3NN0Fi0p'
    }
  };

  const handlePayment = () => {
    if (acceptedTerms) {
      window.open(planDetails.paymentUrls[selectedCurrency], '_blank');
    }
  };

  const termsAndConditions = `
TÉRMINOS Y CONDICIONES

PLAN FULL

Los presentes Términos y Condiciones regulan la contratación y uso del Plan Full, ofrecido por RUNAMATIC (en adelante, "Runamatic"), y constituyen un acuerdo vinculante entre Runamatic y el cliente que contrate el servicio (en adelante, "el Cliente").

Al contratar el Plan Full y efectuar el pago correspondiente, el Cliente declara haber leído, comprendido y aceptado íntegramente estos Términos y Condiciones.

1. Objeto del servicio

Runamatic prestará al Cliente los servicios correspondientes al Plan Full, consistentes en asesoría completa de estrategia comercial, análisis y mejora de procesos, y la implementación, configuración y mantenimiento de automatizaciones personalizadas vinculadas a la plataforma Runamatic.

El Plan Full incluye asesoría estratégica comercial, análisis de procesos y la automatización de todas las funcionalidades nativas de la plataforma, tales como, a modo enunciativo y no limitativo:

• Comportamiento de agente de inteligencia artificial.
• Seguimientos automáticos.
• Integración con el CRM interno.
• Etiquetado automático.
• Segmentación de usuarios.
• Otras herramientas propias de Runamatic.

Si el Cliente desea integrar aplicaciones, plataformas o sistemas externos, dichas integraciones deberán ser cotizadas de manera independiente, evaluando la complejidad de cada caso. El pago de estas integraciones será único, separado del abono mensual del Plan.

2. Vigencia y pagos

El servicio comenzará a partir de la confirmación del primer pago y se renovará automáticamente de manera mensual, salvo cancelación conforme al punto 8.
La duración total dependerá de la complejidad de la cuenta y de las automatizaciones a implementar.

3. Modalidad de trabajo

Una vez recibido el pago, Runamatic creará un grupo de WhatsApp en el que participarán miembros del equipo de Runamatic y el Cliente y/o su equipo, con el fin de coordinar avances y resolver consultas.
El Plan Full tiene como objetivo brindar asesoría estratégica y alto valor operativo, lo que no implica que el personal de Runamatic trabaje en la cuenta del Cliente todos los días de la semana.

4. Plazos y entregas

Runamatic se compromete a brindar actualizaciones sobre la cuenta con un máximo de 3 (tres) días de demora.

Los tiempos de entrega son orientativos y pueden variar por factores ajenos a Runamatic, tales como:

• Configuración o aprobación de cuentas de WhatsApp o Meta.
• Bloqueos o restricciones previas de cuentas.
• Configuración del portfolio comercial de Meta u otros sistemas externos.

5. Obligaciones del Cliente

El Cliente deberá:

• Realizar los ajustes y configuraciones necesarias en sus cuentas de Meta u otros sistemas externos que se integren con Runamatic.
• Facilitar información, accesos y recursos necesarios para la correcta ejecución del servicio.

6. Política de devoluciones

Debido a que el servicio implica costos de infraestructura, recursos y personal, no se realizarán devoluciones una vez abonado el mes en curso.

7. Continuidad del servicio

El Cliente reconoce que, por motivos personales o de fuerza mayor, miembros del equipo de Runamatic pueden no estar disponibles temporalmente. En tal caso, Runamatic asignará un reemplazo para garantizar la continuidad del desarrollo.

Al concluir la configuración inicial, el Cliente podrá continuar en el Plan Full con refinamientos continuos o migrar a un Plan de Mantenimiento para mantener la operación con soporte y actualizaciones puntuales.

8. Gestión y cancelación de la suscripción

La gestión, modificación o cancelación de la suscripción será responsabilidad exclusiva del Cliente y deberá realizarse antes de la renovación automática del mes siguiente.

9. Limitación de responsabilidad

Runamatic no será responsable por demoras o imposibilidad de prestación del servicio derivadas de causas ajenas a su control, tales como bloqueos de cuentas, cambios en políticas de plataformas externas o eventos de fuerza mayor.

10. Aceptación

Al seleccionar la opción "Acepto los Términos y Condiciones" y completar el pago, el Cliente manifiesta su conformidad y aceptación expresa de todos los puntos aquí establecidos.
  `;

  const seoData = {
    title: 'Plan Full - Asesoría Completa y Automatización Personalizada',
    description: 'Contrata el Plan Full de Runamatic y obtén asesoría completa de estrategia comercial más configuración 100% personalizada por nuestro equipo experto.',
    keywords: 'plan full runamatic, asesoría estrategia comercial, automatización personalizada, chatbot personalizado, configuración a medida',
    canonical: '/planFull',
    ogTitle: 'Plan Full - Asesoría Completa + Automatización por Nuestro Equipo',
    ogDescription: 'Asesoría completa de estrategia comercial y configuración 100% personalizada de automatización para tu empresa. $450 USD/mes.',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Plan Full - Runamatic",
      "description": "Asesoría completa de estrategia comercial más configuración 100% personalizada de automatización por nuestro equipo",
      "provider": {
        "@type": "Organization",
        "name": "Runamatic",
        "url": "https://runamatic.io"
      },
      "offers": {
        "@type": "Offer",
        "name": "Plan Full",
        "price": "450",
        "priceCurrency": "USD",
        "description": "Asesoría completa de estrategia comercial + configuración personalizada + grupo de WhatsApp dedicado"
      }
    }
  };

  return (
    <>
      <SEO {...seoData} />
      <Header />
      <main className="runamatic-plan-page">
        <div className="container">

          {/* Header */}
          <div className="rp-header">
            <div className="rp-header__badge">
              <Star size={13} strokeWidth={2.5} />
              Recomendado
            </div>
            <h1 className="rp-title">Plan Full</h1>
            <p className="rp-subtitle">
              Asesoría completa de estrategia comercial, mejora de procesos y optimización. 
              Nuestro equipo analiza y configura toda la automatización necesaria para tu empresa.
            </p>
            <div className="rp-price-preview">
              <span className="rp-price-preview__from">desde</span>
              <span className="rp-price-preview__amount">$450</span>
              <span className="rp-price-preview__period">USD / mes</span>
            </div>
          </div>

          {/* Features grid */}
          <div className="rp-features-section">
            <div className="rp-features-grid">
              <div className="rp-features-col">
                <div className="rp-col-label">
                  <Zap size={14} strokeWidth={2.5} />
                  Asesoría y Configuración Completa
                </div>
                <ul className="rp-feat-list">
                  {planDetails.features.map((feature, index) => (
                    <li key={index} className="rp-feat-item">
                      <span className="rp-feat-check">
                        <Check size={10} strokeWidth={3.5} />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rp-features-col">
                <div className="rp-col-label">
                  <Zap size={14} strokeWidth={2.5} />
                  Funcionalidades Incluidas
                </div>
                <ul className="rp-feat-list">
                  {planDetails.additionalFeatures.map((feature, index) => (
                    <li key={index} className="rp-feat-item">
                      <span className="rp-feat-check">
                        <Check size={10} strokeWidth={3.5} />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rp-highlight-box">
              <ChevronRight size={18} strokeWidth={2.5} className="rp-highlight-icon" />
              <div>
                <strong>¿Por qué elegir el Plan Full?</strong>
                <p>
                  Nuestro equipo analiza tu estrategia comercial actual, propone soluciones tecnológicas 
                  y configura toda la automatización. Presentamos una demo antes de conectar a tus canales 
                  reales y quedamos disponibles para refinar continuamente tu sistema.
                </p>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          <div className="rp-payment-section">
            <h2 className="rp-payment-title">Elige tu forma de pago</h2>

            <div className="rp-currency-selector">
              <button
                className={`rp-currency-btn ${selectedCurrency === 'usd' ? 'active' : ''}`}
                onClick={() => setSelectedCurrency('usd')}
              >
                <span className="rp-currency-flag">🇺🇸</span>
                <div className="rp-currency-info">
                  <span className="rp-currency-label">Dólares (USD)</span>
                  <span className="rp-currency-price">${planDetails.usdPrice}<span className="rp-currency-freq">/mes</span></span>
                  <span className="rp-currency-method">Stripe</span>
                </div>
                {selectedCurrency === 'usd' && <span className="rp-currency-selected-dot" />}
              </button>

              <button
                className={`rp-currency-btn ${selectedCurrency === 'pesos' ? 'active' : ''}`}
                onClick={() => setSelectedCurrency('pesos')}
              >
                <span className="rp-currency-flag">🇦🇷</span>
                <div className="rp-currency-info">
                  <span className="rp-currency-label">Pesos Argentinos</span>
                  <span className="rp-currency-price">
                    ${planDetails.pesosPrice.toLocaleString()}
                    <span className="rp-currency-freq">/mes</span>
                    <span className="rp-currency-iva">+ IVA</span>
                  </span>
                  <span className="rp-currency-method">MercadoPago</span>
                </div>
                {selectedCurrency === 'pesos' && <span className="rp-currency-selected-dot" />}
              </button>
            </div>

            <div className="rp-terms-row">
              <label className="rp-terms-label">
                <input
                  type="checkbox"
                  className="rp-terms-checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
                <span>
                  Acepto los{' '}
                  <button type="button" className="rp-terms-link" onClick={() => setShowTerms(true)}>
                    Términos y Condiciones
                  </button>
                  {' '}del Plan Full
                </span>
              </label>
            </div>

            <button
              className={`rp-pay-btn ${!acceptedTerms ? 'rp-pay-btn--disabled' : ''}`}
              onClick={handlePayment}
              disabled={!acceptedTerms}
            >
              {acceptedTerms
                ? <>
                    Contratar Plan Full
                    <span className="rp-pay-btn__detail">
                      {selectedCurrency === 'usd'
                        ? `$${planDetails.usdPrice} USD/mes — Stripe`
                        : `$${planDetails.pesosPrice.toLocaleString()} ARS/mes — MercadoPago`}
                    </span>
                  </>
                : 'Acepta los términos para continuar'}
            </button>
          </div>
        </div>

        {/* Terms Modal */}
        {showTerms && (
          <div className="runamatic-terms-modal">
            <div className="runamatic-terms-modal-content">
              <div className="runamatic-terms-modal-header">
                <h3>Términos y Condiciones - Plan Full</h3>
                <button
                  className="runamatic-close-btn"
                  onClick={() => setShowTerms(false)}
                >
                  ×
                </button>
              </div>
              <div className="runamatic-terms-modal-body">
                <pre>{termsAndConditions}</pre>
              </div>
              <div className="runamatic-terms-modal-footer">
                <button
                  className="runamatic-accept-terms-btn"
                  onClick={() => {
                    setAcceptedTerms(true);
                    setShowTerms(false);
                  }}
                >
                  Acepto los Términos y Condiciones
                </button>
                <button
                  className="runamatic-close-modal-btn"
                  onClick={() => setShowTerms(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
};

export default PlanRunamaticPage;
